import type { PaintingRecord } from "../types";
import { paintingMatchPromoHtml } from "../copy/wechat-miniprograms";
import { setDetailTitle } from "../meta";
import { escapeHtml } from "../util/html";
import { deferForFastPaint } from "../util/defer";
import { fetchJson } from "../util/fetch-json";
import {
  paintingCardImageCandidates,
  paintingDetailImageCandidates,
  wirePaintingImgFallbacks,
} from "../util/painting-images";

function imgTag(
  p: PaintingRecord,
  urls: string[],
  className: string,
  loading: "lazy" | "eager"
): string {
  const enc = encodeURIComponent(JSON.stringify(urls));
  const first = urls[0] || "";
  return `<img class="${className} art-img-with-fallback" src="${escapeHtml(first)}" data-pfb="${enc}" alt="${escapeHtml(p.title)}" loading="${loading}" decoding="async" />`;
}

export function renderArt(): string {
  return `
    <div id="art-root">
      <div id="art-grid" class="card-grid art-card-grid" aria-live="polite">
        <p class="section-sub" style="margin:0">加载画作目录…</p>
      </div>
    </div>
  `;
}

function artCardImgTag(p: PaintingRecord): string {
  return imgTag(p, paintingCardImageCandidates(p), "card-img art-card-img", "lazy");
}

function artDetailImgTag(p: PaintingRecord): string {
  return imgTag(p, paintingDetailImageCandidates(p), "art-detail-visual-img", "eager");
}

function eraFilters(eras: string[], active: string): string {
  const chips = eras
    .map(
      (era) =>
        `<button type="button" class="filter-chip ${active === era ? "active" : ""}" data-era="${escapeHtml(era)}">${escapeHtml(era)}</button>`
    )
    .join("");
  return `<div class="filter-row art-filter-row" role="tablist" aria-label="按时期筛选">${chips}</div>`;
}

function card(p: PaintingRecord): string {
  const y = p.year === "" || p.year == null ? "—" : String(p.year);
  const teaser = p.intro?.trim() || p.story?.trim() || "";
  return `
    <a class="card card-link art-card" href="/art?painting=${p.id}">
      <div class="art-card-image-wrap">
        ${artCardImgTag(p)}
      </div>
      <div class="card-body">
        <h3>${escapeHtml(p.title)}</h3>
        <div class="card-meta">${escapeHtml(p.artist)} · ${escapeHtml(p.era)} · ${escapeHtml(y)}</div>
        ${teaser ? `<p class="art-card-intro">${escapeHtml(teaser)}</p>` : ""}
      </div>
    </a>
  `;
}

function detailContentBlocks(p: PaintingRecord): string {
  const story = p.story?.trim() ?? "";
  const technique = p.technique?.trim() ?? "";
  const trivia = p.trivia?.trim() ?? "";
  const intro = p.intro?.trim() ?? "";
  const introDup =
    intro &&
    story &&
    (intro === story || story.startsWith(intro) || intro.startsWith(story.slice(0, Math.min(intro.length + 8, story.length))));

  const blocks: string[] = [];

  if (intro && !introDup) {
    blocks.push(`<p class="art-detail-lead">${escapeHtml(intro)}</p>`);
  }

  const fold = (id: string, title: string, body: string, open: boolean) => `
    <details class="art-detail-fold" id="${id}" ${open ? "open" : ""}>
      <summary class="art-detail-fold-summary">${escapeHtml(title)}</summary>
      <p class="detail-body">${escapeHtml(body)}</p>
    </details>`;

  if (story) blocks.push(fold("art-fold-story", "创作背景", story, true));
  if (technique) blocks.push(fold("art-fold-technique", "绘画技法", technique, false));
  if (trivia) blocks.push(fold("art-fold-trivia", "趣味与延伸", trivia, false));

  return blocks.join("\n");
}

function detail(p: PaintingRecord): string {
  const titleEn = p.titleEn?.trim() ? `<p class="detail-sub">${escapeHtml(p.titleEn)}</p>` : "";
  const y = p.year === "" || p.year == null ? "—" : String(p.year);
  const museum = p.museum?.trim()
    ? `<p class="section-sub art-detail-museum">收藏地：${escapeHtml(p.museum)}</p>`
    : "";

  const body = detailContentBlocks(p);

  return `
    <div class="detail-layout art-detail-page">
      <a href="/art" class="text-link back-link">← 返回馆藏</a>
      <header class="art-detail-head">
        <h1 class="section-title art-detail-title">${escapeHtml(p.title)}</h1>
        ${titleEn}
        <p class="detail-sub art-detail-meta">${escapeHtml(p.artist)} · ${escapeHtml(p.era)} · ${escapeHtml(y)}</p>
        ${museum}
      </header>
      <article class="detail-article art-detail">
        <aside class="art-detail-visual" aria-label="画作图像">
          <div class="art-detail-visual-frame">
            ${artDetailImgTag(p)}
          </div>
        </aside>
        <div class="art-detail-copy">
          ${body || '<p class="section-sub">暂无文字介绍。</p>'}
          ${paintingMatchPromoHtml({ compact: true })}
        </div>
      </article>
    </div>
  `;
}

function listView(paintings: PaintingRecord[], era: string): string {
  const eras = ["全部", ...Array.from(new Set(paintings.map((x) => x.era).filter(Boolean))).sort()];
  const filtered = era === "全部" ? paintings : paintings.filter((x) => x.era === era);
  return `
      <div class="art-list-header" style="grid-column: 1 / -1">
        <h1 class="section-title" style="margin:0">名画馆 · 虚拟馆藏</h1>
        <p class="section-sub art-list-lead">按时期浏览经典名画；点进作品可读创作背景。动手配对学习请用微信小程序 <a href="/painting-match/">古典名画对对配</a>。</p>
        ${eraFilters(eras, era)}
      </div>
      <div style="grid-column: 1 / -1">${paintingMatchPromoHtml({ compact: true })}</div>
      ${filtered.map(card).join("")}
    `;
}

export function hydrateArt(container: HTMLElement): void {
  deferForFastPaint(() => {
    void hydrateArtAsync(container);
  });
}

async function hydrateArtAsync(container: HTMLElement): Promise<void> {
  const grid = container.querySelector("#art-grid");
  if (!grid) return;

  const params = new URLSearchParams(window.location.search);
  const idRaw = params.get("painting");
  const id = idRaw != null ? Number(idRaw) : NaN;

  try {
    const data = await fetchJson<{ paintings: PaintingRecord[] }>(
      "/data/paintings.json",
      "名画馆目录"
    );
    if (!grid.isConnected) return;
    if (!grid.isConnected) return;
    const paintings = data.paintings ?? [];

    if (Number.isFinite(id)) {
      const p = paintings.find((x) => x.id === id);
      if (p) {
        if (!grid.isConnected) return;
        grid.classList.remove("card-grid", "art-card-grid");
        grid.classList.add("art-detail-host");
        grid.innerHTML = detail(p);
        setDetailTitle(`${p.title} · 名画馆`);
        wirePaintingImgFallbacks(grid);
        return;
      }
      if (!grid.isConnected) return;
      grid.classList.remove("card-grid", "art-card-grid");
      grid.classList.add("art-detail-host");
      grid.innerHTML = `<p class="section-sub">未找到画作 ID ${escapeHtml(String(idRaw))}。<a href="/art">返回馆藏</a></p>`;
      return;
    }

    let activeEra = sessionStorage.getItem("sanoi-art-era") || "全部";
    if (activeEra !== "全部" && !paintings.some((x) => x.era === activeEra)) activeEra = "全部";

    const renderList = (era: string) => {
      grid.classList.remove("art-detail-host");
      grid.classList.add("card-grid", "art-card-grid");
      grid.innerHTML = listView(paintings, era);
      sessionStorage.setItem("sanoi-art-era", era);
      wirePaintingImgFallbacks(grid);
      grid.querySelectorAll<HTMLButtonElement>(".filter-chip").forEach((btn) => {
        btn.addEventListener("click", () => {
          const next = btn.getAttribute("data-era") || "全部";
          renderList(next);
        });
      });
    };
    renderList(activeEra);
  } catch (e) {
    if (grid.isConnected) {
      grid.classList.remove("art-detail-host");
      const msg = e instanceof Error ? e.message : "馆藏暂时无法加载";
      grid.innerHTML = `<p class="section-sub">${escapeHtml(msg)}。若为本机开发，请在项目根目录执行 <code>npm run content:assemble</code> 后刷新。</p>`;
    }
  }
}
